import { favorites, giftLists } from '@consumer/api'
import FavoritesApi from '@consumer/api/FavoritesApi'
import { useSharedData } from '@consumer/composables/sharedData'
import { indexBy } from '@consumer/helpers/object'
import type {
  Favoritable,
  FavoriteButtonSetup,
  GiftList,
  GiftListItem,
  SyncedFavorite,
} from '@consumer/types/serializers'
import type { Options } from '@shared/services/api'
import { cloneDeep } from '@shared/object'
import { currentParams } from '@shared/url'
import { pick } from 'lodash-es'
import { defineStore } from 'pinia'
import heartIcon from '@consumer/assets/icons/heart.svg?svgicon'
import { asyncOpenModal } from '../modals'
declare global {
  interface Window {
    favoritesStore: ReturnType<typeof initializeFavoritesStore> & { $reset: Function }
  }
}
export const MAX_RECENTLY_ADDED_FAVORITES = 4 // Keep only the last 4 items
export const LOCAL_STORAGE_KEY = 'guest-favorites-store'

function isGiftListItem (item: GiftListItem | Favoritable): item is GiftListItem {
  return (item as GiftListItem).giftListId !== undefined
}

function toFavoritable (item: GiftListItem): Favoritable {
  return {
    favoriteId: item.id,
    giftListItem: item,
    favoritableId: item.listable.id,
    favoritableType: item.listableType,
  }
}

export const useGuestFavoritesStore = defineStore(LOCAL_STORAGE_KEY, () => {
  const guestFavorites = ref<GiftListItem[]>([])
  const { currentUser } = $(useSharedData())

  function addFavorite (favoritable: Favoritable) {
    const currentFavorite = guestFavorites.value.find(item => isSameFavorite(item, favoritable))
    if (currentFavorite) {
      return currentFavorite
    }
    else {
      if (guestFavorites.value.length === MAX_RECENTLY_ADDED_FAVORITES) {
        openLoginOrSignupModal()
        return
      }

      const item = favoritable.giftListItem
      // Surfaces with no favorite button don't serialize it (see FavoritableSerializer).
      if (!item) return

      const currentMetadata = (window as any).currentGift?.giftlyMetadata
      if (currentMetadata) item.listable.giftlyMetadata = currentMetadata

      const giftListItem = {
        ...item,
        id: (new Date()).getTime(),
      } satisfies GiftListItem

      guestFavorites.value.unshift(giftListItem)

      return giftListItem
    }
  }

  function isSameFavorite (item: GiftListItem, favoritable: Favoritable) {
    const other = favoritable.giftListItem
    if (!other) return false

    return item.listable.id === other.listable.id
      && item.listableType === other.listableType
      && item.listable.tagline === other.listable.tagline
  }

  function removeFavorite (favoritable: Favoritable) {
    guestFavorites.value = guestFavorites.value.filter(item => !isSameFavorite(item, favoritable))
  }

  function hasFavorite (favoritable: Favoritable) {
    return guestFavorites.value.some(item => isSameFavorite(item, favoritable))
  }

  function findFavorite (favoritable: Favoritable) {
    return guestFavorites.value.find(item => isSameFavorite(item, favoritable))
  }

  async function openLoginOrSignupModal () {
    await asyncOpenModal(
      import('@consumer/modals/loginOrSignup/LoginOrSignUpModal.vue'),
      {
        icon: heartIcon,
        iconProps: {
          size: '20px',
        },
        title: 'Add to favorites',
        subtitle: 'Before we can add this gift to your favorites, you’ll need to login or create an account. '
            + 'We’ll send you a login code to your email - copy it and you’re done! No passwords, no hassle.',
      },
    )
  }

  async function pushToServer ({ onSync }: { onSync?: (syncedFavorites: SyncedFavorite[]) => void } = {}) {
    // only save guest favorites if the user is logged in
    if (!currentUser || !guestFavorites.value.length) return

    const syncedFavorites = await favorites.syncGuestFavorites<SyncedFavorite[]>({
      data: {
        guestFavorites: guestFavorites.value.map(item => ({
          // We need to pass the id of the item in the local store so that
          // we can send the db id back and map it accordingly
          favoriteId: item.id,
          listableType: item.listableType,
          listableId: item.listable.id,
          tagline: item.listable.tagline,
          giftlyMetadata: item.listable.giftlyMetadata,
        })),
      },
    })

    onSync?.(syncedFavorites)

    guestFavorites.value = []
  }

  return {
    guestFavorites,
    addFavorite,
    hasFavorite,
    removeFavorite,
    pushToServer,
    findFavorite,
    openLoginOrSignupModal,
  }
}, {
  persist: {
    pick: ['guestFavorites'],
    key: LOCAL_STORAGE_KEY,
  },
})

export function useFavoritesStore () {
  if (import.meta.env.SSR)
    return initializeFavoritesStore()

  return window.favoritesStore ||= initializeFavoritesStore()
}

function initializeFavoritesStore () {
  const guestFavoritesStore = useGuestFavoritesStore()
  const { currentUser } = $(useSharedData())
  let recentlyAddedFavorites = $ref<GiftListItem[]>()
  let lastAddedFavorite = $ref<GiftListItem>()
  const lastAddedToList = $ref<{ giftListItem: GiftListItem; message: string }>()
  let isLoading = $ref(true)
  let syncedFavoritesByOldId = $ref<Record<number, SyncedFavorite>>()

  const isEmpty = computed(
    () => !recentlyAddedFavorites || (!isLoading && recentlyAddedFavorites && recentlyAddedFavorites?.length === 0),
  )

  const header = computed(() => {
    if (isLoading) return

    if (recentlyAddedFavorites) {
      if (recentlyAddedFavorites.length > 0) {
        return 'Recently added'
      }
      else {
        return 'Your favorites list is empty'
      }
    }
  })

  async function loadRecentlyAddedFavorites () {
    recentlyAddedFavorites = currentUser
      ? (await FavoritesApi.recentlyAdded<GiftListItem[]>())
      : (cloneDeep(guestFavoritesStore.guestFavorites).slice(0, MAX_RECENTLY_ADDED_FAVORITES) || [])
    isLoading = false
  }

  function addFavorite (
    favoritable: Favoritable,
    params: FavoriteButtonSetup['params'] = undefined,
  ) {
    const addPromise = currentUser
      // Store it in the database
      ? favorites.create({
        data: {
          type: favoritable.favoritableType,
          id: favoritable.favoritableId,
          params: params || pick(Object.fromEntries(currentParams()), ['giftly_design_name']),
        },
      })
      // Store it in the local store
      : new Promise((resolve) => {
        resolve(guestFavoritesStore.addFavorite(favoritable))
      })

    return addPromise.then((favorite: GiftListItem | undefined) => {
      // Add it to the store so that we don't have to reload the list
      if (favorite) {
        if (!recentlyAddedFavorites) {
          recentlyAddedFavorites = [favorite]
        }
        else {
          recentlyAddedFavorites.unshift(favorite)
        }

        recentlyAddedFavorites = recentlyAddedFavorites.slice(
          0,
          MAX_RECENTLY_ADDED_FAVORITES,
        )
        lastAddedFavorite = favorite
      }

      return favorite
    })
  }

  function removeFavorite (item: Favoritable | GiftListItem, options?: Options) {
    const favoritable = isGiftListItem(item) ? toFavoritable(item) : item

    const removePromise = currentUser
      // Remove it from the database
      ? favorites.destroy(
        {
          id: syncedFavoritesByOldId?.[favoritable.favoriteId!]?.newId || favoritable.favoriteId,
          ...options,
        },
      )
      // Remove it from the local storage
      : new Promise((resolve) => {
        resolve(
          guestFavoritesStore.removeFavorite(favoritable),
        )
      })

    return removePromise.then((response) => {
      // Remove it from the store so that we don't have to reload the list
      recentlyAddedFavorites = currentUser
        ? recentlyAddedFavorites?.filter(item => item.id !== favoritable.favoriteId)
        : cloneDeep(guestFavoritesStore.guestFavorites).slice(0, MAX_RECENTLY_ADDED_FAVORITES)

      return response
    })
  }

  async function syncGuestFavorites () {
    // Sync the local storage favorites with the server
    await guestFavoritesStore.pushToServer({
      // If a favorites were actually pushed, refresh the favorites list to use the server favorites.
      onSync: (syncedFavorites: SyncedFavorite[]) => {
        loadRecentlyAddedFavorites()
        syncedFavoritesByOldId = indexBy(syncedFavorites, 'oldId')
      },
    })
  }

  async function openAddToListModal (favorite: GiftListItem, { onOpen }: { onOpen?: () => void } = {}) {
    const userLists = await giftLists.index<GiftList[]>({ visit: false, query: { favoriteId: favorite.id } })
    await asyncOpenModal(
      import('@consumer/modals/favorites/AddToListModal.vue'),
      {
        favorite: cloneDeep(favorite),
        userLists,
      },
    )
    onOpen?.()
  }

  return reactive({
    recentlyAddedFavorites: $$(recentlyAddedFavorites),
    lastAddedFavorite: $$(lastAddedFavorite),
    lastAddedToList: $$(lastAddedToList),
    loadRecentlyAddedFavorites,
    header,
    isEmpty,
    isLoading: $$(isLoading),
    addFavorite,
    removeFavorite,
    syncGuestFavorites,
    openAddToListModal,
    openLoginOrSignupModal: guestFavoritesStore.openLoginOrSignupModal,
    $reset () {
      recentlyAddedFavorites = undefined
    },
  })
}
